// src/components/tokenPage/overview/OrderBookDepthCard.tsx

import React from "react";
import { useBinanceDepthWS } from "@/hooks/useBinanceDepthWS";
import { DataCard } from "@/components/ui";

interface Props {
  symbol: string; // e.g. SOLUSDT or SOL
}

const OrderBookLevel: React.FC<{ price: number; qty: number; pct: number; side: 'bid'|'ask' }> = ({ price, qty, pct, side }) => (
  <div className="relative h-5 w-full rounded overflow-hidden">
    {/* Background bar */}
    <div className="absolute inset-0 bg-gray-800/70">
      <div 
        className={`${side === 'bid' ? 'bg-emerald-500/30' : 'bg-rose-500/30'} h-full`} 
        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} 
      />
    </div>
    {/* Text overlay */}
    <div className="absolute inset-0 flex items-center justify-between px-2 text-[11px] tabular-nums">
      <span className={side === 'bid' ? 'text-emerald-300' : 'text-rose-300'}>
        ${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
      <span className="text-slate-300">
        {qty >= 1000 ? `${(qty / 1000).toFixed(1)}K` : qty.toFixed(2)}
      </span>
    </div>
  </div>
);

const OrderBookDepthCard: React.FC<Props> = ({ symbol }) => {
  const { snapshot, imbalancePct, hasError } = useBinanceDepthWS(symbol);
  const bestBid = snapshot?.bids?.[0]?.price ?? 0;
  const bestAsk = snapshot?.asks?.[0]?.price ?? 0;
  const spreadPct = snapshot?.spreadPct ?? 0;

  // Format spread with at least 2 significant figures, up to 7 decimals max
  const formatSpreadPct = (value: number): string => {
    if (value === 0) return '0.00';
    
    // Get 2 significant figures
    const formatted = Number(value.toPrecision(2));
    
    // Calculate how many decimal places we need for this value
    // to display the 2 sf properly, capped at 7 decimals
    const absValue = Math.abs(formatted);
    let decimals: number;
    
    if (absValue >= 1) {
      decimals = 2;
    } else if (absValue >= 0.1) {
      decimals = 2;
    } else if (absValue >= 0.01) {
      decimals = 3;
    } else if (absValue >= 0.001) {
      decimals = 4;
    } else if (absValue >= 0.0001) {
      decimals = 5;
    } else if (absValue >= 0.00001) {
      decimals = 6;
    } else {
      decimals = 7;
    }
    
    return formatted.toFixed(decimals);
  };

  // Normalize bars by a single max qty across both sides (top N levels)
  const top = 10;
  const maxBid = Math.max(...(snapshot?.bids?.slice(0, top).map(b => b.qty) || [0]));
  const maxAsk = Math.max(...(snapshot?.asks?.slice(0, top).map(a => a.qty) || [0]));
  const globalMax = Math.max(1, maxBid, maxAsk);

  const isLoading = !snapshot && !hasError;

  // Hide card if there's an error or no data (token not available on Binance)
  if (hasError || (!isLoading && !snapshot)) {
    return null;
  }

  return (
    <DataCard
      title="Order Book (Binance)"
      subtitle="Top-of-book, spread and depth"
      icon="panels-left-right"
      iconColor="text-sky-400"
      isLoading={isLoading}
      loadingVariant="card"
    >
      {snapshot && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] text-slate-400 mb-1.5 font-medium">Bids (Buy)</div>
            <div className="space-y-1">
              {snapshot.bids.slice(0, top).map((b, i) => (
                <OrderBookLevel 
                  key={i} 
                  price={b.price} 
                  qty={b.qty} 
                  pct={(b.qty / globalMax) * 100} 
                  side="bid" 
                />
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-slate-400 mb-1.5 font-medium">Asks (Sell)</div>
            <div className="space-y-1">
              {snapshot.asks.slice(0, top).map((a, i) => (
                <OrderBookLevel 
                  key={i} 
                  price={a.price} 
                  qty={a.qty} 
                  pct={(a.qty / globalMax) * 100} 
                  side="ask" 
                />
              ))}
            </div>
          </div>
          <div className="col-span-2 grid grid-cols-3 gap-2 mt-2">
            <Kpi label="Best Bid" value={`$${bestBid.toLocaleString()}`} />
            <Kpi label="Best Ask" value={`$${bestAsk.toLocaleString()}`} />
            <Kpi label="Spread" value={`${formatSpreadPct(spreadPct)}%`} />
          </div>
          <div className="col-span-2">
            <div className="text-[11px] text-slate-400">Imbalance</div>
            <div className={`text-sm font-semibold ${imbalancePct != null ? (imbalancePct > 0 ? 'text-emerald-300' : imbalancePct < 0 ? 'text-rose-300' : 'text-slate-300') : 'text-slate-300'}`}>
              {imbalancePct == null 
                ? 'N/A' 
                : `${imbalancePct > 0 ? '+' : ''}${imbalancePct.toFixed(1)}% (${
                    imbalancePct > 0 ? 'bid-heavy' : imbalancePct < 0 ? 'ask-heavy' : 'balanced'
                  })`
              }
            </div>
          </div>
        </div>
      )}
    </DataCard>
  );
};

const Kpi: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-gray-800/40 rounded-lg p-2.5">
    <div className="text-[10px] text-slate-400">{label}</div>
    <div className="text-sm font-semibold text-slate-50 tabular-nums">{value}</div>
  </div>
);

export default OrderBookDepthCard;


